import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { getPropertyCancellationLabelShape, getPropertyRefundableStatus, getRefundDescriptions } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import PropertyCancellationPolicyView from './PropertyCancellationPolicyView'

interface Props {
  propertyTimeZone: string
  headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'
  headlineCancellationPolicyStart: Date | undefined
  fullyRefundableUntil: string | undefined
  cancellationPolicies: Array<App.Property.CancellationPolicy>
  currency: string
  shouldClarifyRefundIsHotelOnly: boolean
  showCancellationTimeline: boolean
}

function PropertyCancellationPolicy({
  propertyTimeZone,
  headlineCancellationPolicyType,
  headlineCancellationPolicyStart,
  fullyRefundableUntil,
  cancellationPolicies,
  currency,
  shouldClarifyRefundIsHotelOnly,
  showCancellationTimeline,
}: Props) {
  const intl = useIntl()
  const refundDescriptions = useMemo(() => getRefundDescriptions(undefined, intl), [intl])
  const refundableStatus = getPropertyRefundableStatus(headlineCancellationPolicyType)
  const refundDescription = refundDescriptions[refundableStatus]

  const { description: policyDescriptions } = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: fullyRefundableUntil,
    cancellationPolicies: cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      // TODO(HP-1333): Update `lib-refunds` to conform to Customer Portal interface.
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimeZone,
    currencyCode: currency,
    hideExcludesFlights: !shouldClarifyRefundIsHotelOnly,
    locale: intl.locale,
  }), [
    fullyRefundableUntil,
    cancellationPolicies,
    propertyTimeZone,
    currency,
    shouldClarifyRefundIsHotelOnly,
    intl.locale,
  ])

  const labelShape = useMemo(() => getPropertyCancellationLabelShape({
    refundable: refundableStatus,
    headlineCancellationPolicyStart,
    propertyTimezone: propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
  }), [refundableStatus, headlineCancellationPolicyStart, propertyTimeZone, shouldClarifyRefundIsHotelOnly])

  return <PropertyCancellationPolicyView
    refundableStatus={refundableStatus}
    refundDescription={refundDescription}
    labelShape={labelShape}
    policyDescriptions={policyDescriptions}
    timelineDeadline={showCancellationTimeline && labelShape.kind === 'date' ? labelShape.formattedDate : undefined}
  />
}

export default memo(PropertyCancellationPolicy)
